import { Carousel } from "@mantine/carousel";
import { Button, Text, Title } from "@mantine/core";
import { useCompleteOnboardingStep } from "@src/components/onboarding/mutations/complete-onboarding-step";
import { useGetOnboardingStatus } from "@src/components/onboarding/queries/get-onboarding-status";
import { OnboardingStep, StepsFilter } from "@src/gql/graphql";
import clsx from "clsx";
import type { EmblaCarouselType } from "embla-carousel";
import { useEffect, useState } from "react";
import { selectIntroVideosStep } from "./onboarding-step-selectors";
import { useOnboardingIntroVideosModal } from "./use-onboarding-intro-videos-modal";

interface IntroSlide {
	title: string;
	arcadeSrc: string;
	arcadePaddingBottom: string;
}

const slidesData: IntroSlide[] = [
	{
		title: "How to start your first interaction with AI",
		arcadeSrc:
			"https://demo.arcade.software/rlsWXAOZtR1C6xIBt7V5?" +
			"embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
		arcadePaddingBottom: "57.44985673352435%",
	},
	{
		title: "Enrich AI responses with additional context",
		arcadeSrc:
			"https://demo.arcade.software/GxB9fDyyht5lR7hadpcb?" +
			"embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
		arcadePaddingBottom: "58.26645264847512%",
	},
];

interface ArcadeEmbedProps {
	src: string;
	title: string;
	paddingBottom: string;
}

function ArcadeEmbed({ src, title, paddingBottom }: ArcadeEmbedProps) {
	return (
		<div
			className={clsx(
				"relative h-0 w-full max-w-full overflow-hidden rounded-lg bg-white",
				"outline outline-1 outline-[#DDDDDD]",
			)}
			style={{ paddingBottom }}
		>
			<iframe
				className="absolute inset-0 h-full w-full border-0 [color-scheme:light]"
				src={src}
				title={title}
				loading="lazy"
				allow="clipboard-write; fullscreen"
				allowFullScreen
				referrerPolicy="strict-origin-when-cross-origin"
			/>
		</div>
	);
}

function useShowOnboardingIntroVideosModal(): boolean {
	const { data } = useGetOnboardingStatus(StepsFilter.All);

	const introVideosStep = selectIntroVideosStep(data);

	// If the onboarding step is completed, don't show the onboarding modal
	if (!introVideosStep || introVideosStep.isCompleted) {
		return false;
	}

	return true;
}

export function OnboardingIntroVideos() {
	const { open } = useOnboardingIntroVideosModal(false);
	const shouldShow = useShowOnboardingIntroVideosModal();

	useEffect(() => {
		if (shouldShow) {
			open();
		}
	}, [open, shouldShow]);

	return null;
}

interface OnboardingIntroVideosCarouselProps {
	closeable: boolean;
	onClose: () => void;
}

export function OnboardingIntroVideosCarousel({
	closeable,
	onClose,
}: OnboardingIntroVideosCarouselProps) {
	const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);
	const [activeSlide, setActiveSlide] = useState(0);
	const {
		mutate: completeOnboardingStep,
		isPending: isCompletingOnboardingStep,
	} = useCompleteOnboardingStep(() => {
		onClose();
	});

	const handleClose = () => {
		if (closeable) {
			onClose();
		} else {
			completeOnboardingStep(OnboardingStep.IntroVideos);
		}
	};

	const handleSkip = () => {
		completeOnboardingStep(OnboardingStep.IntroVideos);
	};

	const slides = slidesData.map((slide, index) => {
		return (
			<Carousel.Slide key={slide.title}>
				<div
					className={clsx(
						"flex min-h-0 min-w-0 flex-col gap-3 p-3 sm:gap-5 sm:p-5",
					)}
				>
					<div className="min-w-0">
						<ArcadeEmbed
							src={slide.arcadeSrc}
							title={slide.title}
							paddingBottom={slide.arcadePaddingBottom}
						/>
					</div>

					<div className="min-w-0">
						<Text className="select-none text-brand-primary" fz="xs" fw={500}>
							{index + 1} of {slidesData.length}
						</Text>

						<Title
							className="select-none break-words"
							fz={{ base: "lg", sm: "xl" }}
							c="dark"
							fw="bold"
							pt={4}
							lh={1.25}
						>
							{slide.title}
						</Title>
					</div>
				</div>
			</Carousel.Slide>
		);
	});

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
			<div
				className={clsx(
					"flex shrink-0 items-center justify-between gap-2 px-3 py-3",
					"sm:px-5 sm:pt-5",
				)}
			>
				<Button
					variant="subtle"
					color="gray"
					miw={100}
					radius={6}
					onClick={handleSkip}
					loading={isCompletingOnboardingStep}
					disabled={isCompletingOnboardingStep}
				>
					Skip for now
				</Button>
				<div className="flex items-center justify-end gap-4">
					<Button
						variant="subtle"
						color="var(--ui-color-brand-primary)"
						miw={100}
						fz="sm"
						radius={6}
						onClick={() => embla?.scrollPrev()}
						disabled={activeSlide === 0}
						hidden={activeSlide === 0}
					>
						Previous
					</Button>
					<Button
						variant="filled"
						color="var(--ui-color-brand-primary)"
						miw={100}
						radius={6}
						onClick={() => {
							if (activeSlide === slidesData.length - 1) {
								handleClose();
							} else {
								embla?.scrollNext();
							}
						}}
						loading={isCompletingOnboardingStep}
						disabled={isCompletingOnboardingStep}
					>
						{activeSlide === slidesData.length - 1 ? "Finish" : "Next"}
					</Button>
				</div>
			</div>

			<div className="flex min-h-0 flex-1 flex-col overflow-y-auto custom-scrollbar">
				<Carousel
					classNames={{
						root: "flex flex-col",
						indicators: "absolute bottom-4",
					}}
					getEmblaApi={setEmbla}
					onSlideChange={setActiveSlide}
					withIndicators
					withControls={false}
					emblaOptions={{ loop: false }}
				>
					{slides}
				</Carousel>
			</div>
		</div>
	);
}
